import React, { useState } from 'react';
import styled from 'styled-components';
import WorkshopCard from './components/WorkshopCard';
import { generateCertificate } from './utils/certificateGenerator';
import CompletedWorkshopCard from './components/CompletedWorkshopCard';
import WorkshopModal from './components/WorkshopModal';
import LiveWorkshopPanel from './components/LiveWorkshopPanel';
import PreRecordedPanel from './components/PreRecordedPanel';
import { diverseWorkshops } from '../../../data/mock/diverseWorkshops';
import { useNotifications } from '../../../contexts/notification';
import { glassMedium } from '../../../styles/components/common/glassmorphism';
import { OutlineButton } from '../../../styles/components/common/buttons';
import { PageHeader, PageTitle } from '../../../styles/components';
import { floatAnimation } from '../../../styles/components/common/animations';

// Container with enhanced glass effect
const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1400px;
  margin: 0 auto;
`;

// Enhanced filters container with improved glassmorphism
const FiltersContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  ${glassMedium}
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

// Enhanced filter button with improved styling
const FilterButton = styled(OutlineButton)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ active, theme }) => active ? 
    `rgba(${parseInt(theme.colors.primary.slice(1, 3), 16)}, 
           ${parseInt(theme.colors.primary.slice(3, 5), 16)}, 
           ${parseInt(theme.colors.primary.slice(5, 7), 16)}, 0.7)` : 
    'rgba(15, 23, 42, 0.25)'};
  color: ${({ active, theme }) => active ? 'white' : 'rgba(255, 255, 255, 0.9)'};
  border: 1px solid ${({ active, theme }) => active ? 
    'rgba(99, 102, 241, 0.5)' : 
    'rgba(255, 255, 255, 0.1)'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ active, theme }) => active ? 
      `rgba(${parseInt(theme.colors.primary.slice(1, 3), 16)}, 
             ${parseInt(theme.colors.primary.slice(3, 5), 16)}, 
             ${parseInt(theme.colors.primary.slice(5, 7), 16)}, 0.9)` : 
      'rgba(15, 23, 42, 0.4)'};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Enhanced workshops grid with responsive design and animations
const WorkshopsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
  
  > * {
    animation: ${floatAnimation} 0.5s ease-out forwards;
    opacity: 1;
    
    &:nth-child(1) { animation-delay: 0.05s; }
    &:nth-child(2) { animation-delay: 0.1s; }
    &:nth-child(3) { animation-delay: 0.15s; }
    &:nth-child(4) { animation-delay: 0.2s; }
    &:nth-child(5) { animation-delay: 0.25s; }
    &:nth-child(6) { animation-delay: 0.3s; }
    &:nth-child(7) { animation-delay: 0.35s; }
    &:nth-child(8) { animation-delay: 0.4s; }
    &:nth-child(9) { animation-delay: 0.45s; }
    &:nth-child(10) { animation-delay: 0.5s; }
  }
`;

// Empty state component
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  ${glassMedium}
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin: ${({ theme }) => theme.spacing.xl} 0;
  
  h3 {
    color: ${({ theme }) => theme.colors.textOnDark};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
  
  p {
    color: ${({ theme }) => theme.colors.textOnDark};
    opacity: 0.8;
    max-width: 500px;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const WorkshopHub = () => {
  const [filter, setFilter] = useState('all');
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showLivePanel, setShowLivePanel] = useState(false);
  const [showPreRecordedPanel, setShowPreRecordedPanel] = useState(false);
  const [registeredWorkshops, setRegisteredWorkshops] = useState([]);
  const [completedWorkshops, setCompletedWorkshops] = useState(() => {
    const saved = localStorage.getItem('completed-workshops');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default completed workshops if none in storage
    return [
      {
        id: 'WS-2024-001', // Changed ID to avoid conflict with live workshop
        title: 'Modern Web Development with React',
        date: '2025-04-15',
        presenter: 'Dr. Sarah Chen',
        status: 'completed',
        certificate: 'CERT-2025-001',
        rating: 0,
        feedback: ''
      },
      {
        id: 'WS-2025-002',
        title: 'Data Science Fundamentals',
        date: '2025-04-20',
        presenter: 'Prof. Michael Torres',
        status: 'completed',
        certificate: 'CERT-2025-002',
        rating: 0,
        feedback: ''
      }
    ];
  });
  const { addNotification, deleteWorkshopNotifications } = useNotifications();
  
  // Create a combined workshops list that includes both diverseWorkshops and any additional completed workshops
  const allWorkshops = [...diverseWorkshops];
  
  // Add completed workshops that aren't in the diverseWorkshops array
  completedWorkshops.forEach(completedWorkshop => {
    if (!diverseWorkshops.some(w => w.id === completedWorkshop.id)) {
      // Create a workshop object from the completed workshop data
      allWorkshops.push({
        id: completedWorkshop.id,
        title: completedWorkshop.title,
        date: completedWorkshop.date,
        presenter: completedWorkshop.presenter,
        status: 'completed',
        type: 'completed', // Set type for filtering
        // Add basic placeholder data for the card
        description: 'This workshop has been completed.',
        thumbnailUrl: '',
        price: 0
      });
    }
  });
  
  const filteredWorkshops = allWorkshops.filter(workshop => {
    if (filter === 'registered') {
      return registeredWorkshops.includes(workshop.id);
    }
    if (filter === 'completed') {
      // Show workshops that are in the completed list
      return completedWorkshops.some(cw => cw.id === workshop.id);
    }
    if (filter === 'all') return true;
    return workshop.type === filter;
  }).map(workshop => {
    // For completed workshops, merge the workshop data with the completion data
    // This ensures we keep all the original workshop details while marking it completed
    const completedData = completedWorkshops.find(cw => cw.id === workshop.id);
    if (completedData) {
      return {
        ...workshop,
        status: 'completed',
        certificate: completedData.certificate,
        rating: completedData.rating,
        feedback: completedData.feedback
      };
    }
    return workshop;
  });

  const handleRegister = async (workshop) => {
    setRegisteredWorkshops(prev => [...prev, workshop.id]);
    
    // Add notification for workshop registration
    addNotification({
      title: 'Workshop Registration',
      message: `You have successfully registered for "${workshop.title}"`,
      type: 'success',
      data: { workshopId: workshop.id }  // Store workshop ID for navigation
    });
  };
  const handleUnregister = (workshop) => {
    setRegisteredWorkshops(prev => prev.filter(id => id !== workshop.id));
    deleteWorkshopNotifications(workshop.id); // Remove all notifications related to this workshop
  };

  const handleViewDetails = (workshop) => {
    setSelectedWorkshop(workshop);
    setShowModal(true);
  };
  const handleWorkshopAction = (workshop) => {
    if (workshop.type === 'live') {
      setSelectedWorkshop(workshop);
      setShowLivePanel(true);
    } else if (workshop.type === 'upcoming') {
      const isRegistered = registeredWorkshops.includes(workshop.id);
      if (isRegistered) {
        handleUnregister(workshop);
      } else {
        setSelectedWorkshop(workshop);
        setShowModal(true);
      }
    } else if (workshop.type === 'pre-recorded') {
      // Show pre-recorded workshop player
      setSelectedWorkshop(workshop);
      setShowPreRecordedPanel(true);
    }
  };

  const handleWorkshopCompletion = (workshop) => {
    const completedWorkshop = {
      id: workshop.id,
      title: workshop.title,
      date: workshop.date,
      presenter: workshop.presenter,
      status: 'completed',
      certificate: `CERT-${workshop.id}`,
      rating: 0,
      feedback: ''
    };
    
    const updatedCompletedWorkshops = [...completedWorkshops, completedWorkshop];
    setCompletedWorkshops(updatedCompletedWorkshops);
    localStorage.setItem('completed-workshops', JSON.stringify(updatedCompletedWorkshops));
    
    addNotification({
      title: 'Workshop Completed',
      message: `Congratulations! You've completed "${workshop.title}". Don't forget to rate your experience.`,
      type: 'success'
    });
  };

  /**
   * Handle rating submission for completed workshops
   * Note: As per requirements, the rating and feedback are NOT saved.
   * The UI will show the submitted rating temporarily, but data will not persist.
   */
  const handleRate = async (workshopId, { rating, feedback }) => {
    // Don't save the feedback and rating data
    // Just show a notification to simulate submission
    
    addNotification({
      title: 'Rating Submitted',
      message: 'Thank you for your feedback! (This data is not saved as requested)',
      type: 'success'
    });
    
    // No localStorage updates or state changes
  };

  // Conditionally render either the panels or the main content
  if (showLivePanel && selectedWorkshop) {
    return (
      <LiveWorkshopPanel
        workshop={selectedWorkshop}
        onClose={() => {
          setShowLivePanel(false);
          setSelectedWorkshop(null);
        }}
      />
    );
  }
  
  if (showPreRecordedPanel && selectedWorkshop) {
    return (
      <PreRecordedPanel
        workshop={selectedWorkshop}
        onClose={() => {
          setShowPreRecordedPanel(false);
          setSelectedWorkshop(null);
        }}
      />
    );
  }

  // Render main workshop hub content if no panels are shown
  return (
    <Container>
      <PageHeader>
        <PageTitle>Workshop Hub</PageTitle>
      </PageHeader>
      <FiltersContainer>
        <FilterButton
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          All Workshops
        </FilterButton>
        <FilterButton 
          active={filter === 'live'} 
          onClick={() => setFilter('live')}
        >
          Live Now
        </FilterButton>
        <FilterButton 
          active={filter === 'upcoming'} 
          onClick={() => setFilter('upcoming')}
        >
          Upcoming
        </FilterButton>
        <FilterButton 
          active={filter === 'pre-recorded'} 
          onClick={() => setFilter('pre-recorded')}
        >
          Pre-recorded
        </FilterButton>
        <FilterButton 
          active={filter === 'registered'} 
          onClick={() => setFilter('registered')}
        >
          Registered
        </FilterButton>
        <FilterButton 
          active={filter === 'completed'} 
          onClick={() => setFilter('completed')}
        >
          Completed
        </FilterButton>
      </FiltersContainer>
      
      {filteredWorkshops.length === 0 ? (
        <EmptyState>
          <h3>No Workshops Found</h3>
          <p>There are no workshops matching your current filter. Try selecting a different category or check back later for new workshops.</p>
        </EmptyState>
      ) : (
        <WorkshopsGrid>
          {filteredWorkshops.map(workshop => {
            const completedData = completedWorkshops.find(w => w.id === workshop.id);
            
            if (filter === 'completed' && completedData) {
              {/* Use the workshop data plus the completedData */}
              return (
                <CompletedWorkshopCard
                  key={workshop.id}
                  workshop={{
                    ...workshop,
                    // If we have a completed workshop with no details, add the completedData properties
                    ...(workshop.type === 'completed' ? completedData : {})
                  }}
                  completedData={completedData}
                  onViewCertificate={() => generateCertificate(workshop)}
                  onRate={handleRate}
                />
              );
            }
            
            return (
              <WorkshopCard
                key={workshop.id}
                workshop={workshop}
                onClick={handleWorkshopAction}
                onViewDetails={handleViewDetails}
                isRegistered={registeredWorkshops.includes(workshop.id)}
                isCompleted={!!completedData}
              />
            );
          })}
        </WorkshopsGrid>
      )}

      {showModal && selectedWorkshop && (
        <WorkshopModal
          workshop={selectedWorkshop}
          isRegistered={registeredWorkshops.includes(selectedWorkshop.id)}
          completedData={completedWorkshops.find(w => w.id === selectedWorkshop.id)}
          onClose={() => {
            setShowModal(false);
            setSelectedWorkshop(null);
          }}
          onRegister={handleRegister}
          onUnregister={handleUnregister}
          onRate={handleRate}
          onComplete={handleWorkshopCompletion}
          onJoin={(workshop) => {
            setShowModal(false);
            setShowLivePanel(true);
          }}
        />
      )}
    </Container>
  );
};

export default WorkshopHub;
